// NobleSyncIndicator - Top-right forged badge for sync status
// Shows: Synced, Syncing, Offline (pending), Sync error
// Features: Upload/download counts, tap to expand queue
// Design: Industrial badge with metal finish

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, Badge, Popover, List, Typography, Space, Progress } from 'antd';
import {
  CloudSyncOutlined,
  CloudUploadOutlined,
  CloudDownloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WifiOutlined,
  DisconnectOutlined,
} from '@ant-design/icons';
import { NobleColors, NobleSpacing, NobleShadows, NobleTypography } from '../../../theme/noble-tokens';

const { Text } = Typography;

export type SyncStatus = 'synced' | 'syncing' | 'offline' | 'error';

export interface SyncQueueItem {
  id: string;
  type: 'upload' | 'download';
  description: string;
  progress?: number; // 0-100
  timestamp: Date;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  errorMessage?: string;
}

export interface NobleSyncIndicatorProps {
  status?: SyncStatus;
  uploadCount?: number;
  downloadCount?: number;
  queueItems?: SyncQueueItem[];
  lastSyncTime?: Date;
  onClick?: () => void;
  onRetry?: (itemId: string) => void;
  className?: string;
}

// Status colors
const STATUS_CONFIG = {
  synced: {
    color: NobleColors['operational-green-base'],
    icon: <CheckCircleOutlined />,
    label: 'Synced',
    description: 'All changes synchronized',
  },
  syncing: {
    color: NobleColors['command-blue-base'],
    icon: <CloudSyncOutlined spin />,
    label: 'Syncing',
    description: 'Synchronizing changes...',
  },
  offline: {
    color: NobleColors['warning-amber-base'],
    icon: <DisconnectOutlined />,
    label: 'Offline',
    description: 'Changes will sync when online',
  },
  error: {
    color: NobleColors['critical-red-base'],
    icon: <ExclamationCircleOutlined />,
    label: 'Sync Error',
    description: 'Failed to synchronize',
  },
};

// Styled Components
const SyncBadgeContainer = styled(motion.div)<{ $status: SyncStatus }>`
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xs};
  padding: ${NobleSpacing.sm} ${NobleSpacing.md};
  background: ${({ $status }) =>
    $status === 'synced'
      ? `linear-gradient(135deg, ${NobleColors['operational-green-base']}, ${NobleColors['operational-green-dark'] || '#05AA7C'})`
      : $status === 'syncing'
      ? `linear-gradient(135deg, ${NobleColors['command-blue-base']}, ${NobleColors['command-blue-dark']})`
      : $status === 'offline'
      ? `linear-gradient(135deg, ${NobleColors['warning-amber-base']}, ${NobleColors['warning-amber-dark'] || '#E66D00'})`
      : `linear-gradient(135deg, ${NobleColors['critical-red-base']}, ${NobleColors['critical-red-dark'] || '#C72C3B'})`};
  border-radius: 24px;
  box-shadow: ${NobleShadows['elevation-3']};
  cursor: pointer;
  z-index: 1050;
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  min-width: 120px;
  transition: all 0.2s ease-out;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${NobleShadows['elevation-4']};
  }

  &:active {
    transform: scale(0.98);
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
    min-width: 100px;
    padding: ${NobleSpacing.xs} ${NobleSpacing.sm};
  }
`;

const SyncIcon = styled.div`
  font-size: 20px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SyncCounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: white;
  font-family: ${NobleTypography['font-mono']};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
`;

const SyncLabel = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PopoverContent = styled.div`
  width: 360px;
  max-width: 90vw;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${NobleSpacing.md};
  padding-bottom: ${NobleSpacing.sm};
  border-bottom: 1px solid ${NobleColors['steel-gray-base']};
`;

const StatusSummary = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.sm};
  margin-bottom: ${NobleSpacing.md};
  padding: ${NobleSpacing.md};
  background: ${NobleColors['level-1-light']};
  border-radius: 8px;
  border: 1px solid ${NobleColors['steel-gray-base']};
`;

const QueueList = styled(List)`
  max-height: 320px;
  overflow-y: auto;

  .ant-list-item {
    padding: ${NobleSpacing.sm} 0;
    border-bottom: 1px solid ${NobleColors['steel-gray-base']};

    &:last-child {
      border-bottom: none;
    }
  }
`;

const QueueItemWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${NobleSpacing.sm};
  width: 100%;
`;

const QueueItemIcon = styled.div<{ $type: 'upload' | 'download' }>`
  font-size: 16px;
  color: ${({ $type }) =>
    $type === 'upload' ? NobleColors['command-blue-base'] : NobleColors['operational-green-base']};
  margin-top: 2px;
`;

const QueueItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const QueueItemDescription = styled(Text)`
  display: block;
  font-size: 13px;
  color: ${NobleColors['steel-gray-dark']};
  margin-bottom: 4px;
`;

const QueueItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${NobleSpacing.xs};
  font-size: 11px;
  color: ${NobleColors['steel-gray-base']};
`;

const LastSyncTime = styled.div`
  font-size: 11px;
  color: ${NobleColors['steel-gray-base']};
  text-align: center;
  margin-top: ${NobleSpacing.sm};
  padding-top: ${NobleSpacing.sm};
  border-top: 1px solid ${NobleColors['steel-gray-base']};
`;

// Helper function to format relative time
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return date.toLocaleDateString();
};

// Main Component
export const NobleSyncIndicator: React.FC<NobleSyncIndicatorProps> = ({
  status = 'synced',
  uploadCount = 0,
  downloadCount = 0,
  queueItems = [],
  lastSyncTime,
  onClick,
  onRetry,
  className,
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const config = STATUS_CONFIG[status];

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const renderQueueContent = () => (
    <PopoverContent>
      <PopoverHeader>
        <Text strong style={{ fontSize: '16px' }}>
          Sync Queue
        </Text>
        <Space>
          {!isOnline && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <DisconnectOutlined /> Offline
            </Text>
          )}
        </Space>
      </PopoverHeader>

      <StatusSummary>
        <div style={{ fontSize: '24px', color: config.color }}>{config.icon}</div>
        <div style={{ flex: 1 }}>
          <Text strong style={{ display: 'block', fontSize: '14px' }}>
            {config.label}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {config.description}
          </Text>
        </div>
      </StatusSummary>

      {(uploadCount > 0 || downloadCount > 0) && (
        <Space style={{ width: '100%', marginBottom: NobleSpacing.md }}>
          <Badge
            count={uploadCount}
            style={{ backgroundColor: NobleColors['command-blue-base'] }}
            showZero
          >
            <CloudUploadOutlined style={{ fontSize: '20px' }} />
          </Badge>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {uploadCount} pending upload{uploadCount !== 1 ? 's' : ''}
          </Text>
          <Badge
            count={downloadCount}
            style={{ backgroundColor: NobleColors['operational-green-base'] }}
            showZero
          >
            <CloudDownloadOutlined style={{ fontSize: '20px' }} />
          </Badge>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {downloadCount} pending download{downloadCount !== 1 ? 's' : ''}
          </Text>
        </Space>
      )}

      {queueItems.length > 0 ? (
        <QueueList
          dataSource={queueItems}
          renderItem={(itemUnknown) => {
            const item = itemUnknown as SyncQueueItem;
            return (
            <List.Item>
              <QueueItemWrapper>
                <QueueItemIcon $type={item.type}>
                  {item.type === 'upload' ? <CloudUploadOutlined /> : <CloudDownloadOutlined />}
                </QueueItemIcon>
                <QueueItemContent>
                  <QueueItemDescription ellipsis={{ tooltip: item.description }}>
                    {item.description}
                  </QueueItemDescription>
                  {item.progress !== undefined && item.status === 'in-progress' && (
                    <Progress percent={item.progress} size="small" strokeColor={config.color} />
                  )}
                  <QueueItemMeta>
                    <ClockCircleOutlined />
                    {formatRelativeTime(item.timestamp)}
                    {item.status === 'error' && (
                      <Text type="danger" style={{ fontSize: '11px' }}>
                        • {item.errorMessage || 'Failed'}
                      </Text>
                    )}
                  </QueueItemMeta>
                </QueueItemContent>
              </QueueItemWrapper>
            </List.Item>
            );
          }}
        />
      ) : (
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: NobleSpacing.lg }}>
          No pending sync items
        </Text>
      )}

      {lastSyncTime && (
        <LastSyncTime>
          Last synced: {formatRelativeTime(lastSyncTime)}
        </LastSyncTime>
      )}
    </PopoverContent>
  );

  return (
    <Popover
      content={renderQueueContent()}
      title={null}
      trigger="click"
      placement="bottomRight"
      overlayStyle={{ zIndex: 1060 }}
    >
      <SyncBadgeContainer
        $status={status}
        className={className}
        onClick={onClick}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 15 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
      >
        <SyncIcon>{config.icon}</SyncIcon>
        
        {(uploadCount > 0 || downloadCount > 0) ? (
          <SyncCounts>
            {uploadCount > 0 && <div>↑ {uploadCount}</div>}
            {downloadCount > 0 && <div>↓ {downloadCount}</div>}
          </SyncCounts>
        ) : (
          <SyncLabel>{config.label}</SyncLabel>
        )}
      </SyncBadgeContainer>
    </Popover>
  );
};

// Helper hook for managing sync state
export const useNobleSyncState = () => {
  const [status, setStatus] = useState<SyncStatus>('synced');
  const [uploadCount, setUploadCount] = useState(0);
  const [downloadCount, setDownloadCount] = useState(0);
  const [queueItems, setQueueItems] = useState<SyncQueueItem[]>([]);
  const [lastSyncTime, setLastSyncTime] = useState<Date | undefined>(new Date());

  const addToQueue = (item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'status'>) => {
    const newItem: SyncQueueItem = {
      ...item,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      status: 'pending',
    };

    setQueueItems((prev) => [...prev, newItem]);

    if (item.type === 'upload') {
      setUploadCount((prev) => prev + 1);
    } else {
      setDownloadCount((prev) => prev + 1);
    }

    setStatus('offline'); // Assume offline when items are queued
  };

  const removeFromQueue = (itemId: string) => {
    const item = queueItems.find((i) => i.id === itemId);
    if (!item) return;

    setQueueItems((prev) => prev.filter((i) => i.id !== itemId));

    if (item.type === 'upload') {
      setUploadCount((prev) => Math.max(0, prev - 1));
    } else {
      setDownloadCount((prev) => Math.max(0, prev - 1));
    }

    // Update status
    if (queueItems.length === 1) {
      setStatus('synced');
      setLastSyncTime(new Date());
    }
  };

  const startSync = () => {
    setStatus('syncing');
  };

  const completeSync = () => {
    setStatus('synced');
    setQueueItems([]);
    setUploadCount(0);
    setDownloadCount(0);
    setLastSyncTime(new Date());
  };

  const setSyncError = () => {
    setStatus('error');
  };

  return {
    status,
    uploadCount,
    downloadCount,
    queueItems,
    lastSyncTime,
    addToQueue,
    removeFromQueue,
    startSync,
    completeSync,
    setSyncError,
  };
};

